import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, BellRing } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import api, { fmtNum } from "../lib/api";

export default function SettingsPage() {
  const [banks, setBanks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [edits, setEdits] = useState({});
  const [threshold, setThreshold] = useState("");
  const [thresholdSet, setThresholdSet] = useState(false);
  const [aliases, setAliases] = useState([]);
  const [aliasName, setAliasName] = useState("");
  const [aliasVariants, setAliasVariants] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  const acceptSuggestion = async (s) => {
    try {
      await api.post("/partner-aliases", { canonical: s.canonical, variants: s.names.filter((n) => n.length >= 3) });
      toast.success(`Spojeno pod '${s.canonical}' — vrijedi odmah na stranici Partneri`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška pri spajanju");
    }
  };

  const dismissSuggestion = async (s) => {
    await api.post("/partner-aliases/dismiss", { signature: s.signature });
    toast.success("Prijedlog odbačen — više se neće prikazivati");
    load();
  };

  const addAlias = async (e) => {
    e.preventDefault();
    const variants = aliasVariants.split("\n").map((v) => v.trim()).filter(Boolean);
    if (!aliasName.trim() || variants.length === 0) return toast.error("Unesite zajednički naziv i barem jednu varijantu");
    try {
      await api.post("/partner-aliases", { canonical: aliasName.trim(), variants });
      toast.success("Pravilo spajanja dodano — vrijedi odmah na stranici Partneri");
      setAliasName("");
      setAliasVariants("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška pri dodavanju pravila");
    }
  };

  const deleteAlias = async (a) => {
    if (!window.confirm(`Ukloniti pravilo '${a.canonical}'? Ti se partneri više neće spajati pod tim nazivom.`)) return;
    await api.delete(`/partner-aliases/${a.id}`);
    toast.success("Pravilo uklonjeno");
    load();
  };

  const load = () => {
    api.get("/banks").then((r) => setBanks(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/partner-aliases").then((r) => setAliases(r.data)).catch(() => {});
    api.get("/partner-aliases/suggestions").then((r) => setSuggestions(r.data)).catch(() => {});
    api.get("/settings").then((r) => {
      const v = r.data.balance_threshold_eur;
      setThresholdSet(v !== undefined && v !== null);
      setThreshold(v !== undefined && v !== null ? String(v) : "");
    });
  };
  useEffect(load, []);

  const saveThreshold = async () => {
    const v = parseFloat(String(threshold).replace(",", "."));
    if (isNaN(v)) return toast.error("Unesite valjan iznos");
    await api.put("/settings", { balance_threshold_eur: v });
    toast.success("Prag upozorenja spremljen");
    load();
  };

  const clearThreshold = async () => {
    await api.delete("/settings/balance-threshold");
    toast.success("Upozorenje isključeno");
    load();
  };

  const saveBank = async (b) => {
    const e = edits[b.id] || {};
    await api.put(`/banks/${b.id}`, {
      initial_eur: e.initial_eur !== undefined ? parseFloat(e.initial_eur) || 0 : undefined,
      initial_bam: e.initial_bam !== undefined ? parseFloat(e.initial_bam) || 0 : undefined,
    });
    toast.success("Početno stanje spremljeno");
    setEdits((x) => ({ ...x, [b.id]: undefined }));
    load();
  };

  const addCat = async (e) => {
    e.preventDefault();
    if (!newCat.trim()) return;
    try {
      await api.post("/categories", { name: newCat });
      setNewCat("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška");
    }
  };

  const delCat = async (id) => {
    await api.delete(`/categories/${id}`);
    load();
  };

  const setEdit = (bankId, field, val) => setEdits((x) => ({ ...x, [bankId]: { ...(x[bankId] || {}), [field]: val } }));

  const [newBank, setNewBank] = useState({ name: "", short: "", initial_eur: "", initial_bam: "" });
  const addBank = async (e) => {
    e.preventDefault();
    if (!newBank.name.trim()) return toast.error("Unesite naziv banke");
    await api.post("/banks", {
      name: newBank.name.trim(),
      short: newBank.short.trim() || newBank.name.trim().split(" ")[0],
      initial_eur: parseFloat(String(newBank.initial_eur).replace(",", ".")) || 0,
      initial_bam: parseFloat(String(newBank.initial_bam).replace(",", ".")) || 0,
    });
    toast.success("Banka dodana");
    setNewBank({ name: "", short: "", initial_eur: "", initial_bam: "" });
    load();
  };

  return (
    <div className="space-y-8 fade-up" data-testid="settings-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-1">Konfiguracija</p>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A]">Postavke</h1>
      </div>

      <section className="space-y-4">
        <h2 className="font-heading text-xl font-semibold text-[#0F172A]">Banke i početna stanja</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {banks.map((b) => {
            const e = edits[b.id] || {};
            return (
              <div key={b.id} className="bg-white border border-[#E2E8F0] rounded-sm p-5 space-y-3" data-testid={`bank-settings-${b.short}`}>
                <p className="font-semibold text-sm text-[#0F172A]">{b.name}</p>
                <div className="space-y-2">
                  <div>
                    <label className="text-xs text-[#64748B]">Početno stanje EUR</label>
                    <Input
                      data-testid={`bank-initial-eur-${b.short}`}
                      type="number" step="0.01"
                      value={e.initial_eur !== undefined ? e.initial_eur : b.initial_eur}
                      onChange={(ev) => setEdit(b.id, "initial_eur", ev.target.value)}
                      className="tabular-nums"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#64748B]">Početno stanje BAM</label>
                    <Input
                      data-testid={`bank-initial-bam-${b.short}`}
                      type="number" step="0.01"
                      value={e.initial_bam !== undefined ? e.initial_bam : b.initial_bam}
                      onChange={(ev) => setEdit(b.id, "initial_bam", ev.target.value)}
                      className="tabular-nums"
                    />
                  </div>
                </div>
                {edits[b.id] && (
                  <Button data-testid={`save-bank-${b.short}`} size="sm" onClick={() => saveBank(b)} className="bg-[#0D9488] hover:bg-[#0F766E] rounded-sm w-full">
                    <Save className="w-4 h-4 mr-2" /> Spremi
                  </Button>
                )}
                <p className="text-xs text-[#64748B] tabular-nums">Trenutno: {fmtNum(b.initial_eur)} € / {fmtNum(b.initial_bam)} KM</p>
              </div>
            );
          })}
          <form onSubmit={addBank} className="bg-white border border-dashed border-[#9FB2C2] rounded-sm p-5 space-y-2" data-testid="add-bank-form">
            <p className="font-semibold text-sm text-[#0F172A]">Dodaj banku</p>
            <Input data-testid="new-bank-name" placeholder="Naziv banke" value={newBank.name} onChange={(e) => setNewBank((b) => ({ ...b, name: e.target.value }))} />
            <Input data-testid="new-bank-short" placeholder="Kratica (npr. ASA)" value={newBank.short} onChange={(e) => setNewBank((b) => ({ ...b, short: e.target.value }))} />
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="new-bank-eur" type="number" step="0.01" placeholder="Početno EUR" value={newBank.initial_eur} onChange={(e) => setNewBank((b) => ({ ...b, initial_eur: e.target.value }))} className="tabular-nums" />
              <Input data-testid="new-bank-bam" type="number" step="0.01" placeholder="Početno BAM" value={newBank.initial_bam} onChange={(e) => setNewBank((b) => ({ ...b, initial_bam: e.target.value }))} className="tabular-nums" />
            </div>
            <Button data-testid="add-bank-btn" type="submit" size="sm" className="w-full bg-[#0D9488] hover:bg-[#0F766E] rounded-sm">
              <Plus className="w-4 h-4 mr-1" /> Dodaj banku
            </Button>
          </form>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="font-heading text-xl font-semibold text-[#0F172A] flex items-center gap-2">
          <BellRing className="w-5 h-5 text-[#D97706]" /> Upozorenje na nizak saldo
        </h2>
        <div className="bg-white border border-[#E2E8F0] rounded-sm p-5 max-w-lg space-y-3">
          <p className="text-sm text-[#475569]">Ako projekcija ukupnog salda (uključujući planirane transakcije) padne ispod ovog praga u sljedećih 60 dana, na Pregledu se prikazuje upozorenje.</p>
          <div className="flex gap-2 items-center">
            <Input data-testid="threshold-input" type="number" step="0.01" placeholder="npr. 5000" value={threshold} onChange={(e) => setThreshold(e.target.value)} className="tabular-nums max-w-[180px]" />
            <span className="text-sm text-[#475569]">EUR</span>
            <Button data-testid="save-threshold-btn" onClick={saveThreshold} className="bg-[#0D9488] hover:bg-[#0F766E] rounded-sm">
              <Save className="w-4 h-4 mr-2" /> Spremi
            </Button>
            {thresholdSet && (
              <Button data-testid="clear-threshold-btn" onClick={clearThreshold} variant="outline" className="rounded-sm text-[#DC2626] border-[#DC2626]/40 hover:bg-[#DC2626]/5">
                Isključi
              </Button>
            )}
          </div>
          {thresholdSet && <p className="text-xs text-[#64748B]">Upozorenje je aktivno.</p>}
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="font-heading text-xl font-semibold text-[#0F172A]">Kategorije</h2>
        <form onSubmit={addCat} className="flex gap-2 max-w-md">
          <Input data-testid="new-category-input" value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Nova kategorija..." />
          <Button data-testid="add-category-btn" type="submit" className="bg-[#0D9488] hover:bg-[#0F766E] rounded-sm shrink-0">
            <Plus className="w-4 h-4" />
          </Button>
        </form>
        <div className="flex flex-wrap gap-2" data-testid="categories-list">
          {categories.map((c) => (
            <Badge key={c.id} className="bg-white border border-[#E2E8F0] text-[#0F172A] hover:bg-[#F1F5F9] rounded-sm px-3 py-1.5 text-sm font-normal gap-2">
              {c.name}
              <button data-testid={`delete-category-${c.name}`} onClick={() => delCat(c.id)} className="text-[#64748B] hover:text-[#DC2626] transition-colors duration-200">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </Badge>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="font-heading text-xl font-semibold text-[#0F172A]">Spajanje partnera — vlastita pravila</h2>
        <p className="text-sm text-[#475569] max-w-2xl">
          Sami odredite koja se imena partnera prikazuju kao JEDAN partner na stranici Partneri. Upišite zajednički naziv i varijante
          (jednu po retku) — svaki naziv partnera koji sadrži sve riječi neke varijante grupira se pod zajednički naziv
          (kvačice i velika/mala slova se ne razlikuju). Transakcije se ne mijenjaju — spajanje vrijedi za prikaz u Partnerima.
        </p>
        {suggestions.length > 0 && (
          <div className="space-y-2 max-w-2xl border border-[#D97706]/40 bg-[#D97706]/5 rounded-sm p-4" data-testid="alias-suggestions">
            <p className="text-sm font-semibold text-[#0F172A]">
              Prijedlozi spajanja ({suggestions.length}) — slična imena koja bi mogla biti isti partner:
            </p>
            {suggestions.map((s, i) => (
              <div key={s.signature} data-testid={`alias-suggestion-${i}`} className="flex items-start justify-between gap-3 bg-white border border-[#E2E8F0] rounded-sm px-4 py-3">
                <div className="min-w-0">
                  <p className="font-medium text-sm text-[#0F172A]">{s.canonical} <span className="font-normal text-[#64748B]">· {s.count} transakcija</span>{s.ai_verified && (
                    <span data-testid={`alias-suggestion-ai-${i}`} className="ml-1.5 inline-block align-middle rounded-sm bg-[#0D9488]/10 text-[#0D9488] text-[10px] font-medium px-1.5 py-0.5">AI potvrđeno</span>
                  )}</p>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {s.names.map((n) => (
                      <Badge key={n} className="rounded-sm text-[11px] font-normal bg-[#F1F5F9] text-[#475569] hover:bg-[#F1F5F9]">{n}</Badge>
                    ))}
                  </div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button
                    data-testid={`alias-suggest-accept-${i}`}
                    onClick={() => acceptSuggestion(s)}
                    className="h-8 px-3 rounded-sm bg-[#0D9488] hover:bg-[#0F766E] text-white text-xs font-medium transition-colors duration-200"
                  >
                    Spoji
                  </button>
                  <button
                    data-testid={`alias-suggest-dismiss-${i}`}
                    onClick={() => dismissSuggestion(s)}
                    className="h-8 px-3 rounded-sm border border-[#CBD5E1] hover:border-[#DC2626] hover:text-[#DC2626] text-[#475569] text-xs font-medium transition-colors duration-200"
                  >
                    Odbaci
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {aliases.length > 0 && (
          <div className="space-y-2 max-w-2xl" data-testid="alias-list">
            {aliases.map((a) => (
              <div key={a.id} data-testid={`alias-row-${a.id}`} className="flex items-start justify-between gap-3 bg-white border border-[#E2E8F0] rounded-sm px-4 py-3">
                <div className="min-w-0">
                  <p className="font-medium text-sm text-[#0F172A]">{a.canonical}</p>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {a.variants.map((v) => (
                      <Badge key={v} className="rounded-sm text-[11px] font-normal bg-[#F1F5F9] text-[#475569] hover:bg-[#F1F5F9]">{v}</Badge>
                    ))}
                  </div>
                  {a.created_by && <p className="text-[11px] text-[#94A3B8] mt-1.5">dodao: {a.created_by}</p>}
                </div>
                <button data-testid={`alias-delete-${a.id}`} onClick={() => deleteAlias(a)} className="p-2 text-[#64748B] hover:text-[#DC2626] transition-colors duration-200 shrink-0" title="Ukloni pravilo">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        <form onSubmit={addAlias} className="space-y-2 max-w-2xl">
          <Input
            data-testid="alias-canonical-input"
            placeholder="Zajednički naziv (npr. Fizička osoba)"
            value={aliasName}
            onChange={(e) => setAliasName(e.target.value)}
          />
          <Textarea
            data-testid="alias-variants-input"
            rows={3}
            placeholder={"Varijante naziva — jedna po retku, npr.:\nBASIC DARKO\nBasic Darko Kralja Tomislava Mostar"}
            value={aliasVariants}
            onChange={(e) => setAliasVariants(e.target.value)}
          />
          <Button data-testid="alias-add-btn" type="submit" className="rounded-sm bg-[#0D9488] hover:bg-[#0F766E]">
            <Plus className="w-4 h-4 mr-1" /> Dodaj pravilo
          </Button>
        </form>
      </section>
    </div>
  );
}
